import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import {
  selectClinicId,
  selectClinicProfile,
  selectTherapistId,
  selectTherapistName,
} from "../../store/slices/authSlice";
import { useGetAppointmentsQuery } from "../../store/services/calendarApi";
import { useGetRoomsResolvedAvailabilityForRangeQuery } from "../../store/services/roomsApi";
import { useGetClinicTherapistsQuery } from "../../store/services/therapistApi";
import { useGetPatientsQuery, PATIENTS_PICKER_PAGE_SIZE } from "../../store/services/patientsApi";
import { isAssignableTherapist } from "../../config/permissionsRegistry";
import {
  useVisibilityRefresh,
  DEFAULT_POLLING_INTERVAL,
} from "../../hooks/useRealtimeQuery";
import { trackFeature, FEATURE_KEYS } from "../../services/telemetry";
import { buildCalendarHours, getClinicCalendarStartHour } from "../../utils/calendarHours";
import { getAppointmentStatusInfo, buildTreatmentTypeMap } from "../../utils/appointmentDisplay";
import { useGetCategoriesQuery } from "../../store/services/tasksApi";
import {
  formatLocalDateString,
  HEBREW_DAYS_SHORT,
  HEBREW_DAYS_FULL,
} from "../../utils/dateUtils";
import { getHolidayForDate } from "../../utils/israeliHolidays";
import { getOverlapLayout } from "./WeekView";
import CurrentTimeLine from "./CurrentTimeLine";
import AppointmentModal from "./AppointmentEntryModal";
import HoverTip from "../ui/HoverTip";
import ModalCloseButton from "../ui/ModalCloseButton";
import ContentLoader from "../shared/ContentLoader";
import {
  CaretLeft,
  CaretRight,
  CalendarBlank,
  Door,
  DoorOpen,
  ArrowClockwise,
  Eye,
  EyeSlash,
  PlusCircle,
  User,
  Clock,
  WheelchairMotion,
  Question,
} from "@phosphor-icons/react";
import "./RoomsCalendarModal.css";

const MOBILE_BREAKPOINT = 768;
const UNASSIGNED_KEY = "__unassigned__";
const UNASSIGNED_PREF_KEY = "clinikit_rooms_calendar_unassigned";

// Same palette/order as ClinicCalendarModal so a therapist keeps one color
// across the clinic calendar and the rooms calendar.
const THERAPIST_COLORS = [
  "#4dd9b4", "#e91e63", "#ff9800", "#9c27b0", "#3f51b5", "#00bcd4",
  "#f44336", "#8bc34a", "#795548", "#607d8b", "#ffc107", "#ab47bc",
];

const VIEW_OPTIONS = [
  { value: "day", label: "יום" },
  { value: "week", label: "שבוע" },
];

const formatTime = (date) =>
  new Date(date).toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit" });

const formatHoursLabel = (minutes) => {
  if (!minutes) return "0 ש'";
  const h = minutes / 60;
  return `${Number.isInteger(h) ? h : h.toFixed(1)} ש'`;
};

const readUnassignedPref = () => {
  try {
    return localStorage.getItem(UNASSIGNED_PREF_KEY) === "1";
  } catch {
    return false;
  }
};

/**
 * RoomsCalendarModal — clinic-wide calendar split BY ROOM.
 *
 * Day / week grid where every day is divided into one column per active room
 * (plus an optional "ללא חדר" column for appointments without a room). Each
 * room column shows its resolved availability (closed hours hatched), its
 * appointments as compact blocks colored by status with a therapist stripe,
 * a per-day utilization summary, and a hover card with the full details.
 *
 * Clicking an empty open slot creates an appointment pre-assigned to that
 * room; clicking a block opens it for edit. Rendered from AdminCalendarPage
 * only when the clinic has 2+ active rooms.
 */
function RoomsCalendarModal({ rooms = [], onClose, initialView = "day" }) {
  const clinicId = useSelector(selectClinicId);
  const clinicProfile = useSelector(selectClinicProfile);
  const adminTherapistId = useSelector(selectTherapistId);
  const adminTherapistName = useSelector(selectTherapistName);

  const [viewMode, setViewMode] = useState(initialView);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [hiddenRoomIds, setHiddenRoomIds] = useState(() => new Set());
  const [showUnassigned, setShowUnassigned] = useState(readUnassignedPref);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [prefilledDate, setPrefilledDate] = useState(null);
  const [prefilledRoomId, setPrefilledRoomId] = useState(null);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= MOBILE_BREAKPOINT : false
  );

  useEffect(() => {
    trackFeature(FEATURE_KEYS.ROOMS);
  }, []);

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth <= MOBILE_BREAKPOINT;
      setIsMobile(mobile);
      if (mobile) setViewMode("day");
    };
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Body scroll lock + Escape (only when the inner AppointmentModal is closed —
  // it owns its own Escape / unsaved-changes handling).
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape" && !showAppointmentModal) onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose, showAppointmentModal]);

  // ---- Grid geometry ----
  const FIRST_HOUR = getClinicCalendarStartHour(clinicProfile);
  const HOURS = useMemo(() => buildCalendarHours(FIRST_HOUR), [FIRST_HOUR]);
  const slotHeight = viewMode === "day" ? 56 : 44;
  const gridHeight = HOURS.length * slotHeight;

  // ---- Date range ----
  const dateRange = useMemo(() => {
    const current = new Date(currentDate);
    if (viewMode === "day") {
      const start = new Date(current.getFullYear(), current.getMonth(), current.getDate(), 0, 0, 0);
      const end = new Date(current.getFullYear(), current.getMonth(), current.getDate(), 23, 59, 59, 999);
      return { start, end };
    }
    const start = new Date(current);
    start.setDate(current.getDate() - current.getDay());
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  }, [currentDate, viewMode]);

  const days = useMemo(() => {
    const count = viewMode === "day" ? 1 : 7;
    return Array.from({ length: count }, (_, i) => {
      const d = new Date(dateRange.start);
      d.setDate(dateRange.start.getDate() + i);
      return d;
    });
  }, [dateRange, viewMode]);

  const rangeKey = `${viewMode}:${formatLocalDateString(dateRange.start)}`;

  // ---- Data ----
  const {
    data: allAppointments = [],
    isLoading,
    isFetching,
    refetch,
  } = useGetAppointmentsQuery(
    {
      clinicId,
      startDate: dateRange.start.toISOString(),
      endDate: dateRange.end.toISOString(),
    },
    { skip: !clinicId, pollingInterval: DEFAULT_POLLING_INTERVAL }
  );
  useVisibilityRefresh(refetch);

  const activeRooms = useMemo(
    () => rooms.filter((r) => r.isActive !== false),
    [rooms]
  );
  const roomIds = useMemo(() => activeRooms.map((r) => r.id), [activeRooms]);

  const { data: availabilityByRoom = {} } = useGetRoomsResolvedAvailabilityForRangeQuery(
    {
      clinicId,
      roomIds,
      startDate: dateRange.start.toISOString(),
      endDate: dateRange.end.toISOString(),
    },
    { skip: !clinicId || roomIds.length === 0 }
  );

  // Availability lookup: roomId → dateStr → { mode, slots }
  const availabilityIndex = useMemo(() => {
    const idx = {};
    Object.entries(availabilityByRoom).forEach(([roomId, daysArr]) => {
      idx[roomId] = {};
      (daysArr || []).forEach((d) => {
        idx[roomId][d.date] = d;
      });
    });
    return idx;
  }, [availabilityByRoom]);

  const getRoomHourState = useCallback(
    (roomId, dateStr, hour) => {
      const day = availabilityIndex[roomId]?.[dateStr];
      if (!day) return null;
      if (day.mode === "open") return "open";
      if (day.mode === "closed" || day.mode === "holiday") return "closed";
      const within = (day.slots || []).some((slot) => {
        const sh = parseInt(slot.start.split(":")[0], 10);
        const eh = parseInt(slot.end.split(":")[0], 10);
        return hour >= sh && hour < eh;
      });
      return within ? "open" : "closed";
    },
    [availabilityIndex]
  );

  const { data: clinicTherapists = [] } = useGetClinicTherapistsQuery(
    { clinicId },
    { skip: !clinicId }
  );
  const activeTherapists = useMemo(
    () => clinicTherapists.filter(isAssignableTherapist),
    [clinicTherapists]
  );
  const therapistColorMap = useMemo(() => {
    const map = {};
    [...activeTherapists]
      .sort((a, b) => (a.uid || "").localeCompare(b.uid || ""))
      .forEach((t, idx) => {
        map[t.uid] = {
          color: THERAPIST_COLORS[idx % THERAPIST_COLORS.length],
          name: `${t.firstName || ""} ${t.lastName || ""}`.trim() || "מטפל",
        };
      });
    return map;
  }, [activeTherapists]);

  const defaultTargetTherapist = useMemo(() => {
    if (!activeTherapists.length) return null;
    return activeTherapists.find((t) => t.uid === adminTherapistId) || activeTherapists[0];
  }, [activeTherapists, adminTherapistId]);

  // Treatment type names for the hover card.
  const { data: treatmentTypes = [] } = useGetCategoriesQuery(
    { clinicId, type: "treatmentType" },
    { skip: !clinicId }
  );
  const treatmentTypeMap = useMemo(() => buildTreatmentTypeMap(treatmentTypes), [treatmentTypes]);

  // Patients prime the scheduling modal dropdown (admin page — always allowed).
  const { data: patientsData } = useGetPatientsQuery(
    { clinicId, pageSize: PATIENTS_PICKER_PAGE_SIZE },
    { skip: !clinicId }
  );

  // ---- Columns (rooms + optional unassigned) ----
  const visibleRooms = useMemo(
    () => activeRooms.filter((r) => !hiddenRoomIds.has(r.id)),
    [activeRooms, hiddenRoomIds]
  );
  const unassignedAppointments = useMemo(
    () => allAppointments.filter((a) => !a.roomId),
    [allAppointments]
  );
  const columns = useMemo(() => {
    const cols = visibleRooms.map((r) => ({ key: r.id, room: r }));
    if (showUnassigned) cols.push({ key: UNASSIGNED_KEY, room: null });
    return cols;
  }, [visibleRooms, showUnassigned]);

  // ---- Positioned appointments: dateStr → columnKey → [apt+layout] ----
  const positioned = useMemo(() => {
    const result = {};
    days.forEach((day) => {
      const dateStr = formatLocalDateString(day);
      result[dateStr] = {};
      const dayAppts = allAppointments.filter(
        (a) => formatLocalDateString(new Date(a.startTime)) === dateStr
      );
      columns.forEach(({ key }) => {
        const colAppts = dayAppts.filter((a) =>
          key === UNASSIGNED_KEY ? !a.roomId : a.roomId === key
        );
        const layout = getOverlapLayout(colAppts);
        result[dateStr][key] = colAppts.map((apt) => {
          const start = new Date(apt.startTime);
          const end = new Date(apt.endTime);
          const startMinutes = start.getHours() * 60 + start.getMinutes();
          const topPx = Math.max(0, ((startMinutes - FIRST_HOUR * 60) / 60) * slotHeight);
          const durationMinutes = Math.max(0, (end - start) / 60000);
          const heightPx = Math.min(
            Math.max((durationMinutes / 60) * slotHeight, 16),
            gridHeight - topPx
          );
          const overlap = layout[apt.id] || { col: 0, totalCols: 1 };
          return { ...apt, topPx, heightPx, durationMinutes, overlap };
        });
      });
    });
    return result;
  }, [allAppointments, days, columns, FIRST_HOUR, slotHeight, gridHeight]);

  // Per-column-day stats: count + booked minutes (cancelled/blocked excluded).
  const statsFor = useCallback(
    (dateStr, key) => {
      const list = positioned[dateStr]?.[key] || [];
      const active = list.filter((a) => a.status !== "cancelled");
      const minutes = active
        .filter((a) => a.isBlockedTime !== true)
        .reduce((sum, a) => sum + a.durationMinutes, 0);
      return { count: active.length, minutes };
    },
    [positioned]
  );

  const dayTotals = useCallback(
    (dateStr) =>
      columns.reduce((sum, { key }) => sum + statsFor(dateStr, key).count, 0),
    [columns, statsFor]
  );

  const totalVisible = useMemo(
    () =>
      days.reduce((sum, d) => sum + dayTotals(formatLocalDateString(d)), 0),
    [days, dayTotals]
  );

  // Auto-scroll once per range to just above the earliest meeting.
  const bodyRef = useRef(null);
  const autoScrolledKeyRef = useRef(null);
  useEffect(() => {
    if (isLoading || autoScrolledKeyRef.current === rangeKey) return;
    const tops = Object.values(positioned)
      .flatMap((byCol) => Object.values(byCol).flat())
      .map((a) => a.topPx);
    if (!tops.length) return;
    autoScrolledKeyRef.current = rangeKey;
    bodyRef.current?.scrollTo({
      top: Math.max(0, Math.min(...tops) - slotHeight),
      behavior: "smooth",
    });
  }, [positioned, rangeKey, slotHeight, isLoading]);

  // ---- Navigation ----
  const shift = (dir) => {
    const next = new Date(currentDate);
    next.setDate(next.getDate() + dir * (viewMode === "day" ? 1 : 7));
    setCurrentDate(next);
  };
  const goToToday = () => setCurrentDate(new Date());

  const toggleRoom = (roomId) => {
    setHiddenRoomIds((prev) => {
      const next = new Set(prev);
      if (next.has(roomId)) next.delete(roomId);
      else next.add(roomId);
      return next;
    });
  };

  const toggleUnassigned = () => {
    setShowUnassigned((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(UNASSIGNED_PREF_KEY, next ? "1" : "0");
      } catch {
        /* storage unavailable — keep in-memory state only */
      }
      return next;
    });
  };

  // ---- Appointment modal ----
  const handleSlotClick = (day, hour, roomKey) => {
    const slotDate = new Date(day);
    slotDate.setHours(hour, 0, 0, 0);
    setPrefilledDate(slotDate);
    setPrefilledRoomId(roomKey === UNASSIGNED_KEY ? null : roomKey);
    setSelectedAppointment(null);
    setShowAppointmentModal(true);
  };

  const handleAppointmentClick = (apt) => {
    setSelectedAppointment(apt);
    setPrefilledDate(null);
    setPrefilledRoomId(null);
    setShowAppointmentModal(true);
  };

  const handleCloseAppointmentModal = () => {
    setShowAppointmentModal(false);
    setSelectedAppointment(null);
    setPrefilledDate(null);
    setPrefilledRoomId(null);
  };

  // ---- Header text ----
  const formatHeaderDate = () => {
    if (viewMode === "day") {
      return currentDate.toLocaleDateString("he-IL", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    }
    const { start, end } = dateRange;
    if (start.getMonth() === end.getMonth()) {
      return `${start.getDate()}-${end.getDate()} ${start.toLocaleDateString("he-IL", {
        month: "long",
        year: "numeric",
      })}`;
    }
    return `${start.toLocaleDateString("he-IL", { day: "numeric", month: "long" })} - ${end.toLocaleDateString(
      "he-IL",
      { day: "numeric", month: "long", year: "numeric" }
    )}`;
  };

  const todayStr = formatLocalDateString(new Date());
  const now = Date.now();
  const colCount = columns.length;
  const gridStyle = {
    gridTemplateColumns: `var(--rcal-time-w) repeat(${Math.max(1, days.length * colCount)}, minmax(var(--rcal-col-w), 1fr))`,
  };

  const renderTip = (apt) => {
    const status = getAppointmentStatusInfo(apt);
    const StatusIcon = status.icon;
    const roomName = apt.roomId
      ? activeRooms.find((r) => r.id === apt.roomId)?.name || "חדר"
      : "ללא חדר";
    const therapistName =
      apt.therapistName || therapistColorMap[apt.therapistId]?.name || "";
    const isBlocked = apt.isBlockedTime === true;
    const typeName = treatmentTypeMap[apt.treatmentTypeId]?.name;
    return (
      <div className="rcal-tip">
        <div className="rcal-tip-title">
          {isBlocked ? `זמן חסום${apt.blockCause ? ` · ${apt.blockCause}` : ""}` : apt.patientName || "פגישה"}
        </div>
        <div className="rcal-tip-row">
          <Clock size={12} weight="bold" />
          <span>
            {formatTime(apt.startTime)}–{formatTime(apt.endTime)} · {apt.durationMinutes} דק'
          </span>
        </div>
        {therapistName && (
          <div className="rcal-tip-row">
            <User size={12} weight="bold" />
            <span>{therapistName}</span>
          </div>
        )}
        <div className="rcal-tip-row">
          <Door size={12} weight="bold" />
          <span>{roomName}</span>
        </div>
        {typeName && (
          <div className="rcal-tip-row">
            <CalendarBlank size={12} weight="bold" />
            <span>{typeName}</span>
          </div>
        )}
        <div className={`rcal-tip-status st-${status.color}`}>
          <StatusIcon size={12} weight="bold" />
          <span>{status.label}</span>
          {apt.status === "cancelled" && apt.cancellationReason && (
            <span> · {apt.cancellationReason}</span>
          )}
        </div>
      </div>
    );
  };

  const renderAppointment = (apt, dateStr) => {
    const status = getAppointmentStatusInfo(apt);
    const therapistColor = therapistColorMap[apt.therapistId]?.color || "transparent";
    const tall = apt.heightPx >= 38;
    const isCancelled = apt.status === "cancelled";
    const showTherapist = tall && !apt.isBlockedTime && apt.therapistName;
    const style = {
      top: `${apt.topPx}px`,
      height: `${apt.heightPx}px`,
      "--rcal-therapist": therapistColor,
      ...(apt.overlap.totalCols > 1
        ? {
            left: `${(apt.overlap.col / apt.overlap.totalCols) * 100}%`,
            right: "auto",
            width: `calc(${(100 / apt.overlap.totalCols).toFixed(2)}% - 2px)`,
          }
        : {}),
    };
    return (
      <div key={apt.id} className="rcal-apt-wrap" style={style}>
        <HoverTip text={renderTip(apt)} className="rcal-apt-anchor" placement="bottom">
          <button
            type="button"
            className={`rcal-apt st-${status.color}${isCancelled ? " cancelled" : ""}${tall ? "" : " tight"}`}
            onClick={(e) => {
              e.stopPropagation();
              handleAppointmentClick(apt);
            }}
            aria-label={`${apt.patientName || "פגישה"} ${formatTime(apt.startTime)}`}
          >
            <span className="rcal-apt-line">
              <span className="rcal-apt-time">{formatTime(apt.startTime)}</span>
              <span className="rcal-apt-name">
                {apt.isBlockedTime ? "זמן חסום" : apt.patientName || "פגישה"}
              </span>
            </span>
            {showTherapist && (
              <span className="rcal-apt-therapist">{apt.therapistName}</span>
            )}
          </button>
        </HoverTip>
      </div>
    );
  };

  return (
    <div
      className="rcal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget && !showAppointmentModal) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="יומן קליניקה לפי חדרים"
    >
      <div className="rcal-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="rcal-header">
          <div className="rcal-header-right">
            <div className="rcal-icon">
              <DoorOpen size={22} weight="fill" />
            </div>
            <div className="rcal-title-text">
              <span className="rcal-eyebrow">יומן קליניקה</span>
              <h2>לפי חדרים</h2>
            </div>
          </div>

          <div className="rcal-header-center">
            <div className="rcal-date-nav">
              <button type="button" className="rcal-nav-btn" onClick={() => shift(-1)} title="הקודם">
                <CaretRight size={16} weight="bold" />
              </button>
              <button type="button" className="rcal-today-btn" onClick={goToToday}>
                <CalendarBlank size={14} weight="regular" />
                <span>היום</span>
              </button>
              <button type="button" className="rcal-nav-btn" onClick={() => shift(1)} title="הבא">
                <CaretLeft size={16} weight="bold" />
              </button>
            </div>
            <h3 className="rcal-date-display">{formatHeaderDate()}</h3>
          </div>

          <div className="rcal-header-left">
            <button
              type="button"
              className="rcal-refresh-btn"
              onClick={() => refetch()}
              disabled={isFetching}
              title="רענן נתונים"
            >
              <ArrowClockwise size={16} weight="bold" className={isFetching ? "spinning" : ""} />
            </button>
            <ModalCloseButton onClick={onClose} />
          </div>
        </div>

        {/* Toolbar */}
        <div className="rcal-toolbar">
          {!isMobile && (
            <div className="rcal-view-toggle" role="tablist" aria-label="תצוגה">
              {VIEW_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  role="tab"
                  aria-selected={viewMode === opt.value}
                  className={`rcal-view-btn ${viewMode === opt.value ? "active" : ""}`}
                  onClick={() => setViewMode(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}

          <div className="rcal-stats">
            <CalendarBlank size={14} weight="fill" />
            <span>
              <strong>{totalVisible}</strong> פגישות בתצוגה
            </span>
          </div>

          <div className="rcal-legend" role="group" aria-label="חדרים">
            {activeRooms.map((room) => {
              const hidden = hiddenRoomIds.has(room.id);
              return (
                <button
                  key={room.id}
                  type="button"
                  className={`rcal-chip ${hidden ? "hidden" : ""}`}
                  onClick={() => toggleRoom(room.id)}
                  title={hidden ? `הצג את ${room.name}` : `הסתר זמנית את ${room.name}`}
                >
                  <Door size={12} weight="fill" />
                  <span className="rcal-chip-name">{room.name}</span>
                  {hidden ? <EyeSlash size={12} weight="bold" /> : <Eye size={12} weight="bold" />}
                </button>
              );
            })}
            <button
              type="button"
              className={`rcal-chip rcal-chip-unassigned ${showUnassigned ? "" : "hidden"}`}
              onClick={toggleUnassigned}
              title={
                showUnassigned
                  ? "הסתר את עמודת הפגישות ללא חדר"
                  : "הצג עמודה של פגישות שלא שובץ להן חדר"
              }
            >
              <Question size={12} weight="bold" />
              <span className="rcal-chip-name">ללא חדר</span>
              {unassignedAppointments.length > 0 && (
                <span className="rcal-chip-count">{unassignedAppointments.length}</span>
              )}
              {showUnassigned ? <Eye size={12} weight="bold" /> : <EyeSlash size={12} weight="bold" />}
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="rcal-body" ref={bodyRef}>
          {isLoading ? (
            <ContentLoader fill message="טוען יומן חדרים..." />
          ) : colCount === 0 ? (
            <div className="rcal-empty">
              <Door size={40} weight="duotone" />
              <p>כל החדרים מוסתרים</p>
              <span>בחרו חדר אחד לפחות בשורת החדרים למעלה כדי לראות את היומן</span>
            </div>
          ) : (
            <div
              className={`rcal-grid view-${viewMode}`}
              style={gridStyle}
            >
              {/* Row 1 — day headers */}
              <div className="rcal-corner rcal-sticky-top rcal-sticky-start" />
              {days.map((day, dayIdx) => {
                const dateStr = formatLocalDateString(day);
                const holiday = getHolidayForDate(day);
                const total = dayTotals(dateStr);
                return (
                  <div
                    key={dateStr}
                    className={`rcal-day-head rcal-sticky-top ${dateStr === todayStr ? "today" : ""} ${dayIdx === 0 ? "first-day" : ""}`}
                    style={{ gridColumn: `span ${colCount}` }}
                  >
                    <span className="rcal-day-name">
                      {viewMode === "day" ? `יום ${HEBREW_DAYS_FULL[day.getDay()]}` : `${HEBREW_DAYS_SHORT[day.getDay()]}'`}
                    </span>
                    <span className="rcal-day-date">
                      {day.getDate()}
                      {viewMode === "day" && ` ${day.toLocaleDateString("he-IL", { month: "long" })}`}
                    </span>
                    {holiday && (
                      <span className={`rcal-holiday ${holiday.severity}`} title={holiday.name}>
                        {holiday.name}
                      </span>
                    )}
                    <span className="rcal-day-count" title="פגישות ביום זה">
                      {total}
                    </span>
                  </div>
                );
              })}

              {/* Row 2 — room headers */}
              <div className="rcal-corner rcal-sticky-top2 rcal-sticky-start" />
              {days.map((day, dayIdx) => {
                const dateStr = formatLocalDateString(day);
                return columns.map(({ key, room }, colIdx) => {
                  const stats = statsFor(dateStr, key);
                  const dayState = room ? availabilityIndex[room.id]?.[dateStr]?.mode : null;
                  const closedAllDay = dayState === "closed" || dayState === "holiday";
                  return (
                    <div
                      key={`${dateStr}-${key}`}
                      className={`rcal-room-head rcal-sticky-top2 ${colIdx === 0 ? "day-start" : ""} ${dayIdx === 0 && colIdx === 0 ? "first-day" : ""} ${
                        room ? "" : "unassigned"
                      } ${closedAllDay ? "closed" : ""}`}
                      title={
                        room
                          ? `${room.name}${room.isAccessible ? " · נגיש" : ""} · קיבולת ${room.capacity || 1}`
                          : "פגישות שלא שובץ להן חדר"
                      }
                    >
                      <span className="rcal-room-name">
                        {room ? (
                          <Door size={11} weight="fill" />
                        ) : (
                          <Question size={11} weight="bold" />
                        )}
                        <span>{room ? room.name : "ללא חדר"}</span>
                        {room?.isAccessible && <WheelchairMotion size={10} weight="bold" />}
                      </span>
                      <span className="rcal-room-stats">
                        {closedAllDay ? (
                          <span className="rcal-room-closed">סגור</span>
                        ) : (
                          <>
                            <span>{stats.count}</span>
                            <span className="rcal-dot">·</span>
                            <span>{formatHoursLabel(stats.minutes)}</span>
                          </>
                        )}
                      </span>
                    </div>
                  );
                });
              })}

              {/* Row 3 — time column + room columns */}
              <div className="rcal-time-col rcal-sticky-start" style={{ height: gridHeight }}>
                {HOURS.map((hour) => (
                  <div key={hour} className="rcal-time-label" style={{ height: slotHeight }}>
                    {String(hour).padStart(2, "0")}:00
                  </div>
                ))}
              </div>
              {days.map((day, dayIdx) => {
                const dateStr = formatLocalDateString(day);
                const isToday = dateStr === todayStr;
                return columns.map(({ key, room }, colIdx) => (
                  <div
                    key={`${dateStr}-${key}`}
                    className={`rcal-col ${colIdx === 0 ? "day-start" : ""} ${dayIdx === 0 && colIdx === 0 ? "first-day" : ""} ${room ? "" : "unassigned"}`}
                    style={{ height: gridHeight }}
                  >
                    {HOURS.map((hour) => {
                      const slotEnd = new Date(day);
                      slotEnd.setHours(hour + 1, 0, 0, 0);
                      const isPast = slotEnd.getTime() < now;
                      const state = room ? getRoomHourState(room.id, dateStr, hour) : null;
                      const clickable = !isPast;
                      const tooltip = isPast
                        ? "זמן זה כבר עבר"
                        : `יום ${HEBREW_DAYS_FULL[day.getDay()]}, ${day.toLocaleDateString("he-IL", {
                            day: "numeric",
                            month: "long",
                          })} | ${String(hour).padStart(2, "0")}:00${
                            room ? ` · ${room.name}` : ""
                          }${state === "closed" ? " | החדר סגור" : ""}`;
                      return (
                        <div
                          key={hour}
                          className={`rcal-slot ${isPast ? "past" : ""} ${state ? `room-${state}` : ""}`}
                          style={{ height: slotHeight }}
                          title={tooltip}
                          onClick={() => clickable && handleSlotClick(day, hour, key)}
                        />
                      );
                    })}
                    <div className="rcal-overlay-apts">
                      {(positioned[dateStr]?.[key] || []).map((apt) => renderAppointment(apt, dateStr))}
                    </div>
                    {isToday && (
                      <CurrentTimeLine
                        firstHour={FIRST_HOUR}
                        lastHour={FIRST_HOUR + HOURS.length}
                        slotHeight={slotHeight}
                      />
                    )}
                  </div>
                ));
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="rcal-footer">
          <div className="rcal-footer-hint">
            <PlusCircle size={14} weight="fill" />
            <span>לחיצה על משבצת ריקה יוצרת פגישה בחדר הזה · ריחוף על פגישה מציג פרטים</span>
          </div>
          {activeTherapists.length > 0 && (
            <div className="rcal-therapist-legend" aria-label="צבעי מטפלים">
              {activeTherapists.map((t) => (
                <span key={t.uid} className="rcal-therapist-item">
                  <span
                    className="rcal-therapist-dot"
                    style={{ backgroundColor: therapistColorMap[t.uid]?.color }}
                  />
                  {therapistColorMap[t.uid]?.name}
                </span>
              ))}
            </div>
          )}
        </div>

        {showAppointmentModal && (
          <AppointmentModal
            appointment={selectedAppointment}
            prefilledDate={prefilledDate}
            patients={patientsData?.patients || []}
            onClose={handleCloseAppointmentModal}
            targetTherapistId={
              selectedAppointment
                ? selectedAppointment.therapistId || null
                : defaultTargetTherapist?.uid || adminTherapistId
            }
            targetTherapistName={
              selectedAppointment
                ? selectedAppointment.therapistName || null
                : defaultTargetTherapist
                ? `${defaultTargetTherapist.firstName || ""} ${defaultTargetTherapist.lastName || ""}`.trim()
                : adminTherapistName
            }
            scheduledByAdminId={adminTherapistId}
            scheduledByAdminName={adminTherapistName}
            preSelectedRoomId={prefilledRoomId}
            onSuccess={() => refetch()}
          />
        )}
      </div>
    </div>
  );
}

export default RoomsCalendarModal;
